import { isDevHost } from "./availability";
import type { ResolveTenantAppResponse } from "./types";

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "https://api.v3.metaship.ai";

/**
 * The hostname to resolve against `GET /tenant-apps/resolve`. Production always
 * uses the real hostname the shell was served on. Dev overrides, checked in
 * order: `?tenantHost=` on the URL (per-tab, no rebuild) → `VITE_TENANT_HOST`
 * (per-checkout default) → the real hostname (so a plain `localhost` still
 * resolves — and fails cleanly with `found:false` — instead of throwing).
 * Both overrides are gated behind `isDevHost()` — a `?tenantHost=` query
 * param on a REAL tenant hostname must never let a visitor spoof a different
 * tenant's branding/modules on a live production domain.
 */
export function resolveTenantHost(location: Pick<Location, "search" | "hostname"> = window.location): string {
  const dev = isDevHost(location.hostname, import.meta.env.DEV);
  if (!dev) return location.hostname;
  const fromQuery = new URLSearchParams(location.search).get("tenantHost");
  if (fromQuery) return fromQuery;
  const fromEnv = import.meta.env.VITE_TENANT_HOST;
  return fromEnv || location.hostname;
}

export async function fetchTenantApp(
  host: string,
  apiBase: string = API_BASE_URL,
): Promise<ResolveTenantAppResponse> {
  const res = await fetch(`${apiBase}/tenant-apps/resolve?host=${encodeURIComponent(host)}`, {
    headers: { "Time-Zone": Intl.DateTimeFormat().resolvedOptions().timeZone },
  });
  if (!res.ok) {
    throw new Error(`GET /tenant-apps/resolve failed: ${res.status}`);
  }
  return res.json();
}
